import { useState } from 'react';
import type { ApiClient } from '../../api';
import type {
  MsrGrundlagen as MsrGrundlagenTyp, MsrVdiGrundlagen, MsrEinstellwertvorlage,
} from './typen';
import { KiAnbieter } from './KiAnbieter';
import { Datenpunktvorlagen } from './Datenpunktvorlagen';
import { Funktionsvorlagen } from './Funktionsvorlagen';
import { AspRegeln } from './AspRegeln';
import { Einstellungen } from './Einstellungen';
import { VdiGrundlagen } from './VdiGrundlagen';
import { Einstellwertvorlagen } from './Einstellwertvorlagen';
import { LoadingState } from '../../muster';

/**
 * Reiter „Grundlagen" — Datenpunktvorlagen, Funktionsvorlagen, ASP-Regeln,
 * Einstellungen, KI-Anbieter, VDI 3814 Blatt 4.3 (Vorgabe Teuber + Viel),
 * Einstellwertvorlagen (Abschnitt 14.1). Hält keinen eigenen fachlichen
 * Zustand, nur den Hinweistext nach einem Speichern (jedes PUT wirkt erst
 * beim nächsten Ableitungslauf — bei der VDI-Liste dagegen sofort, siehe
 * `VdiGrundlagen`).
 */
export function Grundlagen({
  api, projektId, grundlagen, vdiGrundlagen, einstellwertvorlagen,
  onNeuLaden, onVdiNeuLaden, onEinstellwertvorlagenNeuLaden,
}: {
  api: ApiClient; projektId: string; grundlagen: MsrGrundlagenTyp;
  vdiGrundlagen: MsrVdiGrundlagen | null;
  einstellwertvorlagen: MsrEinstellwertvorlage[] | null;
  onNeuLaden: () => void;
  onVdiNeuLaden: () => void;
  onEinstellwertvorlagenNeuLaden: () => void;
}) {
  const [hinweis, setHinweis] = useState<string | null>(null);

  const geaendert = () => {
    setHinweis('Änderung wirkt beim nächsten Ableitungslauf.');
    onNeuLaden();
  };

  return (
    <>
      {/* TM-8c: Meldung über aria-live statt eines Hinweiskastens (S1.10 Nr. 7). */}
      {hinweis && <p className="schwach" role="status" aria-live="polite">{hinweis}</p>}

      <Datenpunktvorlagen api={api} projektId={projektId}
        vorlagen={grundlagen.vorlagen} onGeaendert={geaendert} />

      <Funktionsvorlagen api={api} projektId={projektId}
        vorlagen={grundlagen.funktionsvorlagen} onGeaendert={geaendert} />

      <AspRegeln api={api} projektId={projektId}
        regeln={grundlagen.aspregeln} onGeaendert={geaendert} />

      <Einstellungen api={api} projektId={projektId}
        einstellungen={grundlagen.einstellungen} onGeaendert={geaendert} />

      <KiAnbieter ki={grundlagen.ki} />

      {vdiGrundlagen
        ? (
          <VdiGrundlagen api={api} projektId={projektId} grundlagen={vdiGrundlagen}
            onGeaendert={onVdiNeuLaden} />
        )
        : <LoadingState text="VDI 3814 wird abgerufen …" zeilen={5} spalten={3} />}

      {einstellwertvorlagen
        ? (
          <Einstellwertvorlagen api={api} projektId={projektId}
            vorlagen={einstellwertvorlagen} onGeaendert={onEinstellwertvorlagenNeuLaden} />
        )
        : <LoadingState text="Einstellwertvorlagen werden abgerufen …" zeilen={4} spalten={3} />}
    </>
  );
}
